import { z } from 'zod';

import { type AgentMessagePill } from '@air/server-sdk';
import { reportErrorToBugsnag } from '@air/utils-error';

import {
  type WorkspaceAgentTableColumn,
  type WorkspaceAgentTableRow,
} from '~/components/v2/components/workspace/workspace-agent-panel/schemas/workspaceAgentTableArtifact';
import { describeZodIssues } from '~/components/v2/components/workspace/workspace-agent-panel/utils/describeZodIssues';

export type WorkspaceAgentContextSourceFieldValuePill = Extract<AgentMessagePill, { type: 'contextSourceFieldValue' }>;

/**
 * Built from a `@[table:<id>]` citation and hydrated from the artifact, never read from
 * `metadata.pills`, so it has no `PillSchema` entry.
 *
 * @TODO(WORK-937): Backend keeps `table` off `AgentMessagePill` — citations use
 * `AgentMessageArtifactHandle` (`type: 'table'`) per backend #9445. Keep `TablePill` as a
 * client-only hydrated shape; after SDK sync, type `columns` / `rows` from `AgentTableColumn` /
 * `AgentTableRow` instead of `WorkspaceAgentTable*`.
 */
export type TablePill = {
  columns: WorkspaceAgentTableColumn[];
  id: string;
  rows: WorkspaceAgentTableRow[];
  title: string;
  /** Full row count. `rows` may hold only the first page. */
  count: number;
  truncated: boolean;
  type: 'table';
};

/** Subset of `AgentMessagePill` this panel parses; unknown types are dropped in `parsePills`. */
export type WorkspaceAgentMessagePill = Extract<
  AgentMessagePill | TablePill,
  {
    type:
      | 'asset'
      | 'assetList'
      | 'board'
      | 'boardList'
      | 'comment'
      | 'changePreview'
      | 'contextSourceFieldValue'
      | 'table';
  }
>;

/** Wire `metadata.pills` reference types; table artifact chips use `TablePill` elsewhere. */
export type WorkspaceAgentReferenceMessagePill = Exclude<WorkspaceAgentMessagePill, { type: 'table' }>;

type AgentChangePreviewValue = Extract<
  AgentMessagePill,
  { type: 'changePreview' }
>['rows'][number]['changes'][number]['before'];

export type AssetListPill = Extract<AgentMessagePill, { type: 'assetList' }>;
export type BoardListPill = Extract<AgentMessagePill, { type: 'boardList' }>;

/**
 * Each pill's client-generated `id` must match an `@[pill:<id>]` moniker in the message `content`.
 * Shapes are owned by `AgentMessagePill`; schemas are typed against it so drift fails at compile time.
 */
const AssetPillSchema = z.object({
  id: z.string().min(1),
  type: z.literal('asset'),
  assetId: z.string().min(1),
  versionId: z.string().optional(),
});

const AssetListPillItemSchema = z.object({
  assetId: z.string().min(1),
  versionId: z.string().optional(),
});

const ListPillCountSchema = z.number().int().min(1);

const AssetListPillSchema = z.object({
  id: z.string().min(1),
  type: z.literal('assetList'),
  count: ListPillCountSchema,
  items: z.array(AssetListPillItemSchema).optional(),
});

const BoardPillSchema = z.object({
  id: z.string().min(1),
  type: z.literal('board'),
  boardId: z.string().min(1),
});

const BoardListPillItemSchema = z.object({
  boardId: z.string().min(1),
});

const BoardListPillSchema = z.object({
  id: z.string().min(1),
  type: z.literal('boardList'),
  count: ListPillCountSchema,
  items: z.array(BoardListPillItemSchema).optional(),
});

const isWorkspaceAgentListPillType = (type: unknown): type is 'assetList' | 'boardList' =>
  type === 'assetList' || type === 'boardList';

/** Inbound composer chips still send `items` only; never overwrite a declared `count` with `items.length`. */
const withDerivedListCount = (entry: unknown): unknown => {
  if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) {
    return entry;
  }

  if (!('type' in entry) || !isWorkspaceAgentListPillType(entry.type)) {
    return entry;
  }

  if ('count' in entry && typeof entry.count === 'number') {
    return entry;
  }

  if (!('items' in entry) || !Array.isArray(entry.items)) {
    return entry;
  }

  return { ...entry, count: entry.items.length };
};

const CommentPillSchema = z.object({
  id: z.string().min(1),
  type: z.literal('comment'),
  commentId: z.string().min(1),
  discussionId: z.string().min(1),
  assetId: z.string().min(1),
  versionId: z.string().min(1),
  authorName: z.string(),
  authorInitials: z.string().optional(),
  createdAt: z.string(),
  body: z.string(),
  staleVersion: z.boolean().optional(),
});

const ChangePreviewValueSchema: z.ZodType<AgentChangePreviewValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(ChangePreviewValueSchema),
    z.record(z.string(), ChangePreviewValueSchema),
  ]),
);

const ChangePreviewEntitySchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('asset'),
    assetId: z.string().min(1),
    versionId: z.string().optional(),
  }),
  z.object({
    type: z.literal('board'),
    boardId: z.string().min(1),
  }),
]);

const ChangePreviewPropertySchema = z.object({
  property: z.string().min(1),
  label: z.string().optional(),
  before: ChangePreviewValueSchema,
  after: ChangePreviewValueSchema,
});

const ChangePreviewPillRowSchema = z.object({
  entity: ChangePreviewEntitySchema,
  changes: z.array(ChangePreviewPropertySchema).min(1),
});

const ChangePreviewPillSchema = z.object({
  id: z.string().min(1),
  type: z.literal('changePreview'),
  title: z.string(),
  count: z.number().int().min(0),
  truncated: z.boolean(),
  rows: z.array(ChangePreviewPillRowSchema),
  warnings: z.array(z.string()).optional(),
});

/**
 * A single image value of a brand kit (a context source), which the backend resolves itself: the
 * pill names the kit and the value, never the asset behind them, and the backend accepts only a
 * file-type value that still belongs to that kit in that workspace.
 */
const ContextSourceFieldValuePillSchema = z.object({
  id: z.string().min(1),
  type: z.literal('contextSourceFieldValue'),
  contextSourceId: z.string().min(1),
  contextSourceFieldValueId: z.string().min(1),
});

export const PillSchema: z.ZodType<WorkspaceAgentMessagePill> = z.discriminatedUnion('type', [
  AssetPillSchema,
  AssetListPillSchema,
  BoardPillSchema,
  BoardListPillSchema,
  CommentPillSchema,
  ChangePreviewPillSchema,
  ContextSourceFieldValuePillSchema,
]);

export type ChangePreviewPill = Extract<AgentMessagePill, { type: 'changePreview' }>;
export type ChangePreviewPillRow = ChangePreviewPill['rows'][number];

/** The only pill the asset modal can open — see `useOpenWorkspaceAgentAssetPill`. */
export type WorkspaceAgentAssetPill = Extract<WorkspaceAgentMessagePill, { type: 'asset' }>;

export type WorkspaceAgentSingleBoardPill = Extract<WorkspaceAgentMessagePill, { type: 'board' }>;

/** Single asset or list; unlike `WorkspaceAgentAssetPill`, not openable. */
export type WorkspaceAgentAssetLikePill = Extract<WorkspaceAgentMessagePill, { type: 'asset' | 'assetList' }>;

const PillEnvelopeSchema = z.object({
  pills: z.array(z.unknown()).optional(),
});

/** Shared frozen array so pill-less messages keep one prop identity across streamed tokens. */
export const EMPTY_WORKSPACE_AGENT_PILLS: readonly WorkspaceAgentMessagePill[] = Object.freeze([]);

/** Names a dropped entry without putting workspace content in the report. */
const describeDroppedPill = (entry: unknown, index: number): string => {
  const type = entry && typeof entry === 'object' && 'type' in entry ? entry.type : undefined;

  return `${index}:${typeof type === 'string' ? type : 'unknown'}`;
};

/**
 * Validates `metadata.pills` per entry: the backend adds pill types on its own schedule, and one
 * unrecognized type must not erase every pill on the message.
 */
export const parsePills = (metadata: unknown): readonly WorkspaceAgentMessagePill[] => {
  if (metadata === null || metadata === undefined) {
    return EMPTY_WORKSPACE_AGENT_PILLS;
  }

  const envelope = PillEnvelopeSchema.safeParse(metadata);

  if (!envelope.success) {
    // Issues only: the metadata itself is workspace content.
    reportErrorToBugsnag({
      error: new Error('Message pill metadata could not be parsed'),
      context: 'parsePills',
      metadata: { issues: describeZodIssues(envelope.error) },
    });

    return EMPTY_WORKSPACE_AGENT_PILLS;
  }

  const entries = envelope.data.pills;

  if (!entries?.length) {
    return EMPTY_WORKSPACE_AGENT_PILLS;
  }

  const pills: WorkspaceAgentMessagePill[] = [];
  const dropped: string[] = [];

  entries.forEach((entry, index) => {
    const parsed = PillSchema.safeParse(withDerivedListCount(entry));

    if (parsed.success) {
      pills.push(parsed.data);

      return;
    }

    dropped.push(describeDroppedPill(entry, index));
  });

  if (dropped.length > 0) {
    // One report per message, not per entry.
    reportErrorToBugsnag({
      error: new Error('Message pill metadata contained entries this client cannot render'),
      context: 'parsePills',
      metadata: { dropped, keptCount: pills.length, totalCount: entries.length },
    });
  }

  return pills.length > 0 ? pills : EMPTY_WORKSPACE_AGENT_PILLS;
};
